import { useState } from "react";
import { Megaphone, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageBodyMarkdown } from "@/components/messages/message-body-markdown";
import { AnnouncementComments } from "@/components/dashboard/announcement-comments";
import { AnnouncementSocialBar } from "@/components/dashboard/announcement-social-bar";
import {
  clearDashboardPostReaction,
  setDashboardPostReaction,
} from "@/lib/api/dashboard-posts";
import { getAuthUser } from "@/lib/api/token-store";
import {
  defaultThreadExpanded,
  readThreadExpanded,
  writeThreadExpanded,
} from "@/lib/dashboard/comment-threads";
import { applyReactionToggle, planReactionToggle } from "@/lib/dashboard/reactions-helpers";
import {
  patchDashboardPost,
  refreshDashboardPost,
} from "@/lib/stores/dashboard-posts-store";
import { cn } from "@/lib/utils";

// Each post is its own surface, so the feed reads as a stack of notices rather than one long
// ruled document — but a stack of notices, not a stack of PANELS. The background is the
// page's own; the card is drawn by its border and a shallow shadow.
//
// The fill is `--card-raised`, a HALF step above the page: `--card`'s full step (0.23 against
// 0.20 in dark) reads as a lighter panel at this size rather than a lifted card. Light mode
// has no headroom above a pure-white page, so there the token equals `--background` and the
// border plus shadow carry it alone.
//
// The shadow is deliberately SHALLOW, and the y-offset matters more than the blur. What made
// the design's `0 12px 28px rgb(0 0 0/.3)` visible was that a 12px offset aims it straight
// into the 14px gap and a 28px blur then covers the whole of it, so the gap read darker than
// the cards. Reach below the card is roughly offset + blur/2 — here ~4px, comfortably short
// of the gap's 7px midpoint, so the cards lift without the space between them going grey.
// Dark mode needs more opacity than light for the same apparent depth.
const POST_CARD =
  "rounded-[14px] border border-border bg-card-raised shadow-[0_1px_3px_rgb(0_0_0/0.07)] dark:shadow-[0_2px_5px_rgb(0_0_0/0.28)]";

// "Edited" only when updatedAt moved meaningfully past createdAt (create stamps both in
// the same call, but not with the same nanosecond).
function isEdited(post) {
  const created = Date.parse(String(post?.createdAt || ""));
  const updated = Date.parse(String(post?.updatedAt || ""));
  return Number.isFinite(created) && Number.isFinite(updated) && updated - created > 2000;
}

function myDisplayName() {
  const user = getAuthUser();
  const first = String(user?.profile?.firstName || "").trim();
  const last = String(user?.profile?.lastName || "").trim();
  const full = `${first} ${last}`.trim();
  return full || String(user?.name || user?.displayName || "").trim();
}

/**
 * One announcement: the post itself, then a social bar carrying reactions on the left and the
 * comment summary on the right, then the thread when it is expanded.
 *
 * Expansion is per post and remembered per reader. Until they express a preference,
 * `defaultThreadExpanded` keeps deciding from the post itself — so a thread that grows past
 * the fold, or gains a comment they haven't seen, changes its own mind. Only an actual click
 * is written down; persisting the default would freeze a thread shut the first time it was
 * quiet.
 */
function PostRow({
  post,
  isNew,
  canEdit,
  highlightSince,
  formatRelativeTime,
  formatAbsoluteTime,
  onEdit,
  onDelete,
}) {
  const [composerOpen, setComposerOpen] = useState(false);
  // Optimistic count, so a posted comment updates the summary immediately rather than
  // waiting for the feed to come back around. Only trustworthy while the thread is mounted
  // to keep it fresh — see the reset in toggleExpanded.
  const [localCount, setLocalCount] = useState(null);
  const myUserId = String(getAuthUser()?.id || "").trim();
  // undefined until this reader toggles this post; read once, then owned by state.
  const [explicitExpanded, setExplicitExpanded] = useState(() =>
    readThreadExpanded(myUserId, post.id),
  );

  const commentCount = localCount ?? post.commentCount ?? 0;
  const expanded =
    explicitExpanded ??
    defaultThreadExpanded({
      commentCount,
      lastCommentAt: post.lastCommentAt,
      seenSince: highlightSince,
    });
  // The composer keeps the thread mounted even from a collapsed post, so "Comment" on a
  // post with no comments has somewhere to draw.
  const showThread = expanded || composerOpen;

  const setExpanded = (next) => {
    setExplicitExpanded(next);
    writeThreadExpanded(myUserId, post.id, next);
  };

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) return;
    // Folding the thread away should take the half-written box with it.
    setComposerOpen(false);
    // And hand the count back to the feed. The thread is what kept `localCount` current
    // (its fetch and its realtime subscription both unmount with it), so holding on to the
    // last value would freeze the summary at whatever it read on the way out.
    setLocalCount(null);
  };

  const startComment = () => {
    setExpanded(true);
    setComposerOpen(true);
  };

  const toggleReaction = async (emoji) => {
    const plan = planReactionToggle(post.reactions, emoji);
    if (!plan) return;
    // Render the click immediately; the server response (and the realtime echo) reconcile.
    patchDashboardPost(post.id, {
      reactions: applyReactionToggle(post.reactions, emoji, myDisplayName()),
    });
    try {
      if (plan.action === "clear") await clearDashboardPostReaction(post.id);
      else await setDashboardPostReaction(post.id, { emoji: plan.emoji });
    } catch (error) {
      toast.error(error?.message || "Could not save your reaction");
    }
    // Reconcile either way: on success this confirms the server's tally, on failure it rolls
    // the optimistic patch back.
    void refreshDashboardPost(post.id);
  };

  return (
    <li className={cn("group relative px-6 pb-4 pt-[18px]", POST_CARD)}>
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-[15.5px] font-extrabold leading-snug text-foreground">
            {isNew && (
              <span className="mr-2 inline-block translate-y-[-2px] rounded-[5px] bg-rose-600 px-[7px] py-[3.5px] text-[10.5px] font-extrabold leading-none tracking-[0.5px] text-white dark:bg-rose-500">
                NEW
              </span>
            )}
            {post.title}
          </p>
          <MessageBodyMarkdown className="text-sm text-foreground/85">{post.body}</MessageBodyMarkdown>
          <p className="mt-[7px] text-[12.5px] text-muted-foreground">
            {post.authorName || "Staff"}
            {" · "}
            <span title={formatAbsoluteTime(post.createdAt)}>{formatRelativeTime(post.createdAt)}</span>
            {isEdited(post) && (
              <>
                {" · "}
                <span title={formatAbsoluteTime(post.updatedAt)}>edited</span>
              </>
            )}
          </p>
        </div>
        {canEdit && (
          <div className="flex flex-none items-center gap-1 opacity-0 transition-opacity duration-150 focus-within:opacity-100 group-hover:opacity-100">
            <button
              type="button"
              title="Edit announcement"
              onClick={onEdit}
              className="grid h-[30px] w-[30px] cursor-pointer place-items-center rounded-lg text-muted-foreground transition-colors duration-150 hover:bg-accent/60 hover:text-foreground"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Delete announcement"
              onClick={onDelete}
              className="grid h-[30px] w-[30px] cursor-pointer place-items-center rounded-lg text-rose-600 transition-colors duration-150 hover:bg-rose-600/10 dark:text-rose-400"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <AnnouncementSocialBar
        reactions={post.reactions}
        onToggleReaction={toggleReaction}
        commentCount={commentCount}
        lastCommentAt={post.lastCommentAt}
        commentAuthors={post.commentAuthors || []}
        expanded={expanded}
        onToggleExpanded={toggleExpanded}
        onStartComment={startComment}
        formatRelativeTime={formatRelativeTime}
      />

      {showThread && (
        <AnnouncementComments
          postId={post.id}
          canModerate={canEdit}
          formatTime={formatRelativeTime}
          composerOpen={composerOpen}
          onOpenComposer={() => setComposerOpen(true)}
          onCloseComposer={() => setComposerOpen(false)}
          onCountChange={setLocalCount}
        />
      )}
    </li>
  );
}

/**
 * The announcements feed: the Dashboard's main column. News for the whole team, now two-way —
 * every employee can react and comment, while authoring stays gated on `dashboard:manage`
 * (which arrives as `canEdit`).
 *
 * The column itself has no chrome; the posts inside carry it. That is what lets a thread hang
 * off its own post without a divider cutting through the one below.
 */
export function AnnouncementsCard({
  feed,
  isNew,
  highlightSince,
  formatRelativeTime,
  formatAbsoluteTime,
  onNewPost,
  onEditPost,
  onDeletePost,
}) {
  const showSkeleton = !feed.loaded && feed.posts.length === 0;

  return (
    <section className="flex min-h-[280px] flex-1 flex-col min-[1200px]:min-h-0">
      <div className="flex flex-none items-start justify-between gap-4 px-1 pb-4 pt-0.5">
        <div>
          <div className="flex items-center gap-[11px] text-[19px] font-black tracking-[-0.2px]">
            <Megaphone className="h-5 w-5 text-primary" />
            Announcements
          </div>
          <p className="mt-[3px] text-[13px] text-muted-foreground">
            News and updates for the whole team.
          </p>
        </div>
        {feed.canEdit && (
          <Button
            type="button"
            onClick={onNewPost}
            className="h-[37px] rounded-[9px] px-4 text-[13.5px] font-bold"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            New post
          </Button>
        )}
      </div>
      <ScrollArea className="min-h-0 flex-1">
        {/* The horizontal padding is the post cards' shadow gutter — without it the viewport
            clips the spread on both sides. */}
        <ul className="flex flex-col gap-[14px] px-1 pb-1">
          {showSkeleton && (
            <li className={cn("space-y-6 px-6 py-5", POST_CARD)}>
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-5 w-1/3" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ))}
            </li>
          )}
          {!showSkeleton && feed.posts.length === 0 && (
            <li
              className={cn(
                "flex flex-col items-center gap-2.5 px-6 pb-[30px] pt-[34px] text-center",
                POST_CARD,
              )}
            >
              <Megaphone className="h-[26px] w-[26px] text-muted-foreground opacity-70" />
              <p className="text-sm font-extrabold">No announcements yet</p>
              <p className="max-w-[280px] text-[12.5px] text-muted-foreground">
                {feed.canEdit
                  ? "Post the first one for your team."
                  : "News from your team will show up here."}
              </p>
              {feed.canEdit && (
                <Button type="button" variant="outline" onClick={onNewPost}>
                  <Plus className="mr-1.5 h-4 w-4" />
                  New post
                </Button>
              )}
            </li>
          )}
          {feed.posts.map((post) => (
            <PostRow
              key={post.id}
              post={post}
              isNew={isNew(post)}
              canEdit={feed.canEdit}
              highlightSince={highlightSince}
              formatRelativeTime={formatRelativeTime}
              formatAbsoluteTime={formatAbsoluteTime}
              onEdit={() => onEditPost(post)}
              onDelete={() => onDeletePost(post)}
            />
          ))}
        </ul>
      </ScrollArea>
    </section>
  );
}
